import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from '../../utils/compose';
import { withFirebase } from '../Firebase';
import { withAdminAuthorization } from '../Session';
import * as ROUTES from '../../constants/routes';
import './CustomerDetail.css';

class CustomerDetailBase extends Component {
  constructor(props) {
    super(props);
    this.state = {
      agreement: null,
      loading: true,
      error: null,
    };
  }

  componentDidMount() {
    const { agreementId } = this.props.match.params;

    this.unsubscribe = this.props.firebase.db
      .collection('franchiseAgreements')
      .doc(agreementId)
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            this.setState({
              agreement: { id: doc.id, ...doc.data() },
              loading: false,
            });
          } else {
            this.setState({
              error: 'Customer not found',
              loading: false,
            });
          }
        },
        (error) => {
          console.error('Error fetching customer details:', error);
          this.setState({
            error: 'Error loading customer details',
            loading: false,
          });
        }
      );
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  goBack = () => {
    this.props.history.push(ROUTES.ADMIN_PANEL);
  };

  viewAgreement = () => {
    const { agreement } = this.state;
    window.open(`/franchise-agreement/${agreement.id}`, '_blank');
  };

  formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  render() {
    const { agreement, loading, error } = this.state;

    if (loading) {
      return (
        <div className="customer-detail-page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading customer details...</p>
          </div>
        </div>
      );
    }

    if (error || !agreement) {
      return (
        <div className="customer-detail-page">
          <div className="error-container">
            <h2>⚠️ {error || 'Customer not found'}</h2>
            <button onClick={this.goBack} className="btn-back">
              Back to Admin Panel
            </button>
          </div>
        </div>
      );
    }

    const { formData } = agreement;

    return (
      <div className="customer-detail-page">
        {/* Header */}
        <header className="customer-detail-header">
          <div className="header-left">
            <button onClick={this.goBack} className="btn-back-nav">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to Admin Panel</span>
            </button>
            <div className="customer-title">
              <div className="customer-avatar">
                {(formData?.fullName || 'U')[0].toUpperCase()}
              </div>
              <div>
                <h1>{formData?.fullName || 'Unknown Customer'}</h1>
                <p>{formData?.email || 'No email'}</p>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={this.viewAgreement} className="btn-view-agreement">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>View Agreement</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="customer-detail-main">
          {/* Status Cards */}
          <div className="status-cards">
            <div className="status-card">
              <div className="status-label">Agreement Status</div>
              <div className="status-value">
                {agreement.agreedToTerms ? (
                  <span className="badge badge-success">✓ Signed</span>
                ) : (
                  <span className="badge badge-warning">⏳ Pending Signature</span>
                )}
              </div>
            </div>
            <div className="status-card">
              <div className="status-label">Submitted On</div>
              <div className="status-value">{this.formatDate(agreement.createdAt)}</div>
            </div>
            <div className="status-card">
              <div className="status-label">Expires On</div>
              <div className="status-value">{this.formatDate(agreement.expiresAt)}</div>
            </div>
            <div className="status-card">
              <div className="status-label">Payment Status</div>
              <div className="status-value">
                {agreement.paymentCompleted ? (
                  <span className="badge badge-success">✓ Completed</span>
                ) : (
                  <span className="badge badge-pending">⏳ Pending</span>
                )}
              </div>
            </div>
          </div>

          {/* Customer Information Sections */}
          <div className="detail-sections">
            {/* Personal Information */}
            <section className="detail-section">
              <h2 className="section-title">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Personal Information
              </h2>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Full Name</label>
                  <span>{formData?.fullName || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Email Address</label>
                  <span>{formData?.email || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Phone Number</label>
                  <span>{formData?.phone || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Date of Birth / Incorporation</label>
                  <span>{formData?.dateOfBirth || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Aadhaar Number</label>
                  <span>{formData?.aadhaarNumber || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Entity Type</label>
                  <span style={{ textTransform: 'capitalize' }}>{formData?.entityType || 'N/A'}</span>
                </div>
              </div>
            </section>

            {/* Address Information */}
            <section className="detail-section">
              <h2 className="section-title">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Address Information
              </h2>
              <div className="detail-grid">
                <div className="detail-item full-width">
                  <label>Address Line 1</label>
                  <span>{formData?.addressLine1 || 'N/A'}</span>
                </div>
                <div className="detail-item full-width">
                  <label>Address Line 2</label>
                  <span>{formData?.addressLine2 || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>City</label>
                  <span>{formData?.city || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>State</label>
                  <span>{formData?.state || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Pincode</label>
                  <span>{formData?.pincode || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Country</label>
                  <span>{formData?.country || 'India'}</span>
                </div>
              </div>
            </section>

            {/* Business Information */}
            <section className="detail-section">
              <h2 className="section-title">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Business Information
              </h2>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Preferred Location</label>
                  <span>{formData?.preferredLocation || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>RERA Number</label>
                  <span>{formData?.reraNumber || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>PAN Number</label>
                  <span>{formData?.panNumber || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>GST Number</label>
                  <span>{formData?.gstNumber || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Business Registration Number</label>
                  <span>{formData?.businessRegistrationNumber || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Current Team Size</label>
                  <span>{formData?.currentTeamSize || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Years in Real Estate</label>
                  <span>{formData?.yearsInRealEstate || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Investment Budget</label>
                  <span>{formData?.investmentBudget || 'N/A'}</span>
                </div>
              </div>
            </section>

            {/* Vision & Goals */}
            {formData?.visionGoals && (
              <section className="detail-section">
                <h2 className="section-title">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Vision & Goals
                </h2>
                <div className="detail-text">
                  <p>{formData.visionGoals}</p>
                </div>
              </section>
            )}

            {/* Emergency Contact */}
            <section className="detail-section">
              <h2 className="section-title">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Emergency Contact
              </h2>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Contact Name</label>
                  <span>{formData?.emergencyContactName || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Contact Phone</label>
                  <span>{formData?.emergencyContactPhone || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Relationship</label>
                  <span>{formData?.emergencyContactRelationship || 'N/A'}</span>
                </div>
              </div>
            </section>

            {/* Witness Information */}
            <section className="detail-section">
              <h2 className="section-title">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Witness Information
              </h2>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Witness Name</label>
                  <span>{formData?.witnessName || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Witness Contact</label>
                  <span>{formData?.witnessContact || 'N/A'}</span>
                </div>
              </div>
            </section>

            {/* Payment Details */}
            {agreement.paymentCompleted && (
              <section className="detail-section" style={{ 
                background: 'rgba(40, 167, 69, 0.05)',
                borderColor: 'rgba(40, 167, 69, 0.3)'
              }}>
                <h2 className="section-title" style={{ color: '#28a745' }}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Payment Details
                </h2>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Payment ID</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      {agreement.paymentId || 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Order ID</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      {agreement.paymentOrderId || 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Amount Paid</label>
                    <span style={{ color: '#28a745', fontWeight: '700', fontSize: '16px' }}>
                      Rs. {(agreement.amount || 118000).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Payment Completed At</label>
                    <span>{this.formatDate(agreement.paymentCompletedAt)}</span>
                  </div>
                  {agreement.paymentSignature && (
                    <div className="detail-item full-width">
                      <label>Payment Signature</label>
                      <span style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '11px',
                        wordBreak: 'break-all'
                      }}>
                        {agreement.paymentSignature}
                      </span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Payment Failed/Pending Info */}
            {!agreement.paymentCompleted && agreement.paymentInitiated && (
              <section className="detail-section" style={{ 
                background: 'rgba(255, 193, 7, 0.05)',
                borderColor: 'rgba(255, 193, 7, 0.3)'
              }}>
                <h2 className="section-title" style={{ color: '#ffc107' }}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Payment Status
                </h2>
                <div className="detail-text">
                  <p style={{ color: '#ffc107', marginBottom: '12px' }}>
                    <strong>⚠️ Payment Initiated but Not Completed</strong>
                  </p>
                  <p style={{ fontSize: '14px', marginBottom: '8px' }}>
                    Payment initiated at: {this.formatDate(agreement.paymentInitiatedAt)}
                  </p>
                  {agreement.paymentFailed && (
                    <>
                      <p style={{ fontSize: '14px', color: '#ff6b6b', marginBottom: '8px' }}>
                        <strong>Payment Failed</strong>
                      </p>
                      <p style={{ fontSize: '14px' }}>
                        Reason: {agreement.paymentFailureReason || 'Unknown error'}
                      </p>
                      <p style={{ fontSize: '14px' }}>
                        Failed at: {this.formatDate(agreement.paymentFailedAt)}
                      </p>
                    </>
                  )}
                </div>
              </section>
            )}
          </div>
        </main>
      </div>
    );
  }
}

const CustomerDetail = compose(
  withRouter,
  withFirebase,
  withAdminAuthorization,
)(CustomerDetailBase);

export default CustomerDetail;

